import { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { launchEndDate } from "@/lib/launchDate";

/**
 * PineForge.AI — Launch offer countdown.
 *
 * Ticks once per second. When the launch end date has passed the block
 * shows a stable "Launch offer has ended." message; the pricing UI itself
 * is untouched.
 */
function pad(n) {
  return String(n).padStart(2, "0");
}

function computeParts(msLeft) {
  if (msLeft <= 0) return { done: true, days: 0, hours: 0, mins: 0, secs: 0 };
  const secs = Math.floor(msLeft / 1000) % 60;
  const mins = Math.floor(msLeft / 60000) % 60;
  const hours = Math.floor(msLeft / 3600000) % 24;
  const days = Math.floor(msLeft / 86400000);
  return { done: false, days, hours, mins, secs };
}

function Unit({ value, label, isDays }) {
  return (
    <div className="flex flex-col items-center min-w-[54px]">
      <span className="text-2xl sm:text-3xl font-semibold tracking-tight text-white tabular-nums">
        {isDays ? value : pad(value)}
      </span>
      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">
        {label}
      </span>
    </div>
  );
}

export default function LaunchCountdown({ className = "" }) {
  const { t } = useTranslation();
  const end = launchEndDate().getTime();
  const [parts, setParts] = useState(() => computeParts(end - Date.now()));

  useEffect(() => {
    const tick = () => setParts(computeParts(end - Date.now()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [end]);

  if (parts.done) {
    return (
      <div
        data-testid="launch-countdown-ended"
        className={`inline-flex items-center gap-2 rounded-full border border-[#1E212B] bg-[#12141C] px-4 py-2 text-xs font-mono text-zinc-400 ${className}`}
      >
        <Clock className="w-3.5 h-3.5 text-zinc-500" />
        {t("launch_countdown.ended")}
      </div>
    );
  }

  return (
    <div className={`inline-block ${className}`} data-testid="launch-countdown">
      <div className="inline-flex items-center gap-2 text-xs font-mono text-blue-300 mb-3">
        <Clock className="w-3.5 h-3.5" />
        <span className="uppercase tracking-widest">{t("launch_countdown.ends_in")}</span>
      </div>
      <div
        className="flex items-center gap-3 sm:gap-5 rounded-2xl border border-[#1E212B] bg-[#12141C]/80 backdrop-blur px-5 sm:px-7 py-4 shadow-[0_0_40px_-20px_rgba(37,99,235,0.4)]"
        aria-label={t("launch_countdown.aria")}
        role="timer"
      >
        <Unit value={parts.days} label={t("launch_countdown.days")} isDays />
        <span className="text-2xl text-zinc-700 -mt-3">:</span>
        <Unit value={parts.hours} label={t("launch_countdown.hrs")} />
        <span className="text-2xl text-zinc-700 -mt-3">:</span>
        <Unit value={parts.mins} label={t("launch_countdown.min")} />
        <span className="text-2xl text-zinc-700 -mt-3">:</span>
        <Unit value={parts.secs} label={t("launch_countdown.sec")} />
      </div>
      <p className="text-xs sm:text-sm text-zinc-400 mt-3 max-w-md leading-relaxed">
        {t("launch_countdown.sub_note")}
      </p>
    </div>
  );
}
